import { isPlainObject } from 'remeda'
import type { Cleanup } from './helpers/cleanup'
import { GRID_READY_EVENT } from './hooks/grid/utils/initial-reveal'

// Private types ---------------------------------------------------------------

type NavigationDetail = {
  readonly kind: 'initial' | 'redirect' | 'patch'
}

type Navigation = {
  readonly detail: NavigationDetail
  readonly startMs: number
  readonly phase:
    | { readonly type: 'requesting' }
    | { readonly type: 'rendering'; readonly responseMs: number }
}

// Public types ----------------------------------------------------------------

export type NavigationMeasurement = {
  readonly kind: NavigationDetail['kind']
  readonly outcome:
    | 'ready'
    | 'superseded'
    | 'error'
    | 'hidden'
    | 'timeout'
    | 'pagehide'
  readonly page: string
  readonly startMs: number
  readonly endMs: number
  readonly responseMs?: number
}

// Constants -------------------------------------------------------------------

const NAVIGATION_TIMEOUT_MS = 45_000
const MAIN_VIEW = '[data-phx-main]'
const GRID = '[data-component=".grid"]'

// Private functions -----------------------------------------------------------

/** Accept navigation events while excluding form submissions and other actions. */
const isNavigationDetail = (detail: unknown): detail is NavigationDetail =>
  isPlainObject(detail) &&
  (detail.kind === 'initial' ||
    detail.kind === 'redirect' ||
    detail.kind === 'patch')

/** Read custom event data without trusting its library-provided type. */
const eventDetail = (event: Event): unknown =>
  event instanceof CustomEvent ? event.detail : undefined

/** Only content visible in the viewport contributes to this readiness measure. */
const isVisible = (element: Element): boolean => {
  // Keep visibility checks usable in older supported browsers.
  const visible =
    typeof element.checkVisibility === 'function'
      ? element.checkVisibility({
          checkVisibilityCSS: true,
          checkOpacity: true
        })
      : window.getComputedStyle(element).visibility === 'visible'
  const bounds = element.getBoundingClientRect()

  return (
    visible &&
    bounds.width > 0 &&
    bounds.height > 0 &&
    bounds.bottom > 0 &&
    bounds.right > 0 &&
    bounds.top < window.innerHeight &&
    bounds.left < window.innerWidth
  )
}

/** Reuse the grid reveal marker and the sections' existing loading contract. */
const visibleContentReady = (): boolean => {
  const root = document.querySelector(MAIN_VIEW)
  const pending = root?.querySelectorAll(`${GRID}, [aria-busy="true"]`) ?? []

  return (
    root !== null &&
    !Array.from(pending).some(
      (element) =>
        isVisible(element) &&
        (element.getAttribute('aria-busy') === 'true' ||
          !element.querySelector('.ag-root-wrapper[data-ready]'))
    )
  )
}

/** Group measurements by the server's view module, never by customer URLs. */
const currentPage = (): string =>
  document
    .querySelector(`${MAIN_VIEW} [data-page-view]`)
    ?.getAttribute('data-page-view') ?? 'unknown'

// Public functions ------------------------------------------------------------

/** Track one foreground navigation through response and visible content readiness. */
export const setupNavigationTiming = (
  report: (measurement: NavigationMeasurement) => void
): Cleanup => {
  let active: Navigation | undefined
  let frame: number | undefined
  let timeout: number | undefined
  let initialSeen = false

  /** Release scheduled work when a navigation finishes or is replaced. */
  const cancelScheduled = (): void => {
    if (frame !== undefined) {
      window.cancelAnimationFrame(frame)
    }
    if (timeout !== undefined) {
      window.clearTimeout(timeout)
    }
    frame = undefined
    timeout = undefined
  }

  /** Complete each attempt once; canceled attempts never become ready samples. */
  const finish = (outcome: NavigationMeasurement['outcome']): void => {
    if (!active) {
      return
    }
    const navigation = active
    active = undefined
    cancelScheduled()

    report({
      kind: navigation.detail.kind,
      outcome,
      page: navigation.phase.type === 'rendering' ? currentPage() : 'unknown',
      startMs: navigation.startMs,
      endMs: performance.now(),
      responseMs:
        navigation.phase.type === 'rendering'
          ? navigation.phase.responseMs
          : undefined
    })
  }

  /** Check on two successive frames to allow a paint between ready observations. */
  const checkReady = (): void => {
    if (active?.phase.type !== 'rendering' || frame !== undefined) {
      return
    }
    const navigation = active
    frame = window.requestAnimationFrame(() => {
      frame = undefined
      if (active !== navigation || !visibleContentReady()) {
        return
      }
      frame = window.requestAnimationFrame(() => {
        frame = undefined
        if (active === navigation && visibleContentReady()) {
          finish('ready')
        }
      })
    })
  }

  /** Nested view joins belong to the current redirect, not a new navigation. */
  const start = (event: Event): void => {
    const detail = eventDetail(event)
    if (isPlainObject(detail) && detail.kind === 'error') {
      finish('error')
      return
    }
    if (!isNavigationDetail(detail)) {
      return
    }
    const navigation = detail
    if (navigation.kind === 'initial') {
      const skipJoin = initialSeen || active !== undefined
      initialSeen = true
      if (skipJoin) {
        return
      }
    }
    finish('superseded')
    if (document.visibilityState === 'hidden') {
      return
    }

    active = {
      detail: navigation,
      startMs: navigation.kind === 'initial' ? 0 : performance.now(),
      phase: { type: 'requesting' }
    }
    timeout = window.setTimeout(() => finish('timeout'), NAVIGATION_TIMEOUT_MS)
  }

  /** Phoenix pairs start/stop with the same detail object, even for equal URLs. */
  const stop = (event: Event): void => {
    if (
      !active ||
      active.detail !== eventDetail(event) ||
      active.phase.type !== 'requesting'
    ) {
      return
    }

    active = {
      ...active,
      phase: {
        type: 'rendering',
        responseMs: performance.now() - active.startMs
      }
    }
    checkReady()
  }

  /** Background time must not inflate a foreground response measurement. */
  const visibilityChanged = (): void => {
    if (document.visibilityState === 'hidden') {
      finish('hidden')
    }
  }

  /** Record departure without retaining work across a document suspension. */
  const pageHidden = (): void => finish('pagehide')

  window.addEventListener('phx:page-loading-start', start)
  window.addEventListener('phx:page-loading-stop', stop)
  document.addEventListener('phx:update', checkReady)
  document.addEventListener(GRID_READY_EVENT, checkReady)
  document.addEventListener('visibilitychange', visibilityChanged)
  window.addEventListener('pagehide', pageHidden)

  return () => {
    active = undefined
    cancelScheduled()
    window.removeEventListener('phx:page-loading-start', start)
    window.removeEventListener('phx:page-loading-stop', stop)
    document.removeEventListener('phx:update', checkReady)
    document.removeEventListener(GRID_READY_EVENT, checkReady)
    document.removeEventListener('visibilitychange', visibilityChanged)
    window.removeEventListener('pagehide', pageHidden)
  }
}
